<template>
  <div class="ml-data-table ml-card--depth">
    <div class="ml-data-table__toolbar">
      <div id="datatable-toolbar-left">
        <button v-on:click="toggleFilter" class="ml-data-table__toolbar__action">
          <i class="material-icons">filter_list</i>
          <span>Filter</span>
          <span class="ml-data-table__filter__filters-number" v-if="numberOfFilters > 0">{{ numberOfFilters }}</span>
        </button>
        <pop-over :event="filterEvent" :show="showFilter">
          <div class="ml-data-table__filter">
            <div class="d-flex align-items-center justify-content-between ml-data-table__filter__head">
              <button v-on:click="clearFilters" class="ml-btn ml-btn--white ml-btn--sm">Clear</button>
              <div class="ml-data-table__filter__title">Filter</div>
              <button v-on:click="performFilter" class="ml-btn ml-btn--primary ml-btn--sm">Done</button>
            </div>

            <div class="ml-data-table__filter__body">
              <ul class="filter-list">
                <li class="filter-list__item" v-bind:key="index" v-for="(filter, index) in tableFilters">
                  <div class="filter-list__item-label">
                    <input type="checkbox" :id="filter.key" v-model="filter.selected">
                    <label class="mb-0" :for="filter.key">
                      {{ filter.label }}
                    </label>
                  </div>
                  <!-- Date filters -->
                  <div class="filter-list__item-form d-flex flex-column justify-content-between" v-if="filter.selected && filter.type === 'date'">
                    <select :name="`filter-${filter.label}`" :id="`id-${filter.label}`" v-model="filter.mode">
                      <option value="dateBetween">is between</option>
                      <option value="dateLessThan">is before</option>
                      <option value="dateGreaterThan">is after</option>
                    </select>

                    <div class="w-100 d-flex align-items-start pt-3 filter-list_item-form-input-container">
                      <b-datepicker class="px-1 w-50" placeholder="" icon="calendar-today"
                                    v-if="filter.mode === `dateLessThan` || filter.mode === `dateGreaterThan`" v-model="filter.value">
                      </b-datepicker>
                      <b-datepicker class="px-1 w-50" placeholder="" icon="calendar-today"
                                    v-if="filter.mode === `dateBetween`" v-model="filter.min">
                      </b-datepicker>
                      <span class="mx-3" v-if="filter.mode === `dateBetween`">to</span>
                      <b-datepicker class="px-1 w-50" placeholder="" icon="calendar-today"
                                    v-if="filter.mode === `dateBetween`" v-model="filter.max">
                      </b-datepicker>
                    </div>
                  </div>
                  <!-- End of the date filter -->

                  <!-- Number filters -->
                  <div class="filter-list__item-form d-flex flex-column justify-content-between" v-if="filter.selected && filter.type === 'number'">
                    <select :name="`filter-${filter.label}`" :id="`id-${filter.label}`" v-model="filter.mode">
                      <option value="between">is between</option>
                      <option value="lessThan">is less than</option>
                      <option value="greaterThan">is greater than</option>
                    </select>

                    <div class="w-100 d-flex align-items-start pt-3 filter-list_item-form-input-container">
                      <input class="px-1 w-50" type="number" v-if="filter.mode === `lessThan` || filter.mode === `greaterThan`" v-model="filter.value">
                      <input class="px-1 w-50" type="number" v-if="filter.mode === `between`" v-model="filter.min">
                      <span class="mx-3" v-if="filter.mode === `between`">to</span>
                      <input class="px-1 w-50" type="number" v-if="filter.mode === `between`" v-model="filter.max">
                    </div>
                  </div>
                  <!-- End of the number filter -->

                  <!-- Strings filters -->
                  <div class="filter-list__item-form d-flex flex-column justify-content-between" v-if="filter.selected && filter.type === 'string'">
                    <select :name="`filter-${filter.label}`" :id="`id-${filter.label}`" v-model="filter.mode">
                      <option value="like">is like</option>
                      <option value="exact">is exactly</option>
                    </select>

                    <div class="w-100 d-flex align-items-start pt-3 filter-list_item-form-input-container">
                      <input class="px-1 w-50" type="text" v-model="filter.value">
                    </div>
                  </div>
                  <!-- End of the strings filter -->

                  <!-- List filters -->
                  <div class="filter-list__item-form d-flex flex-column justify-content-between" v-if="filter.selected && filter.type === 'list'">
                    <select :name="`filter-${filter.label}`" :id="`id-${filter.label}`" v-model="filter.value">
                      <option v-for="(item, index) in filter.list" :value="item.key" :key="`filter-list-item#${index}`">{{ item.label }}</option>
                    </select>
                  </div>
                  <!-- End of the list filter -->
                </li>
              </ul>
            </div>
          </div>
        </pop-over>
      </div>

      <div id="datatable-toolbar-right">
        <button class="ml-data-table__toolbar__action" v-for="(button, index) in buttons" :key="`action-button#${index}`" @click="$emit(button.event)">
          <span>{{button.name}}</span>
          <i class="material-icons">{{button.icon}}</i>
        </button>

        <button class="ml-data-table__toolbar__action" @click="$emit('export-data', tableFilters)">
          <i class="material-icons">cloud_download</i>
        </button>
      </div>
    </div>
    <table class="table" v-show="rows.length > 0">
      <thead class="ml-data-table__head">
        <tr>
          <td :class="{
              'text-left' : ((index + 1) !== columns.length) || (column.float === 'left'),
              'text-right' : ((index + 1) === columns.length) || (column.float === 'right')
            }"
            v-for="(column, index) in columns" :key="`datatable-column#${index}`"
            v-if="!(column.hide || column.collapsed)">
            {{ column.label }}
          </td>
          <td class="text-center" v-if="hasActions">Actions</td>
        </tr>
      </thead>
      <tbody class="ml-data-table__body">
        <template v-for="(row, index) in rows">
          <tr 
            :class="['ml-data-table__row', {'ml-data-table__row--clickable': hasCollapsable}]" 
            :key="`datatable-row-primary#${index}`">
            <td :class="{
                 'text-right' : ((index + 1) === columns.length) || (column.float === 'right'),
                 'text-left' : (column.float === 'left'),
                 'ml-data-table__row--text-highlight': column.highlight
               }"
              v-for="(column, index) in columns" :key="`datatable-cell#${index}`"
              v-if="!(column.hide || column.collapsed)"
              @click="toggleRow(row)">
              {{ dynamicallyFilter(row[column.key ], column.filter) }}
            </td>
            <td class="action-icon text-center" v-if="hasActions">
              <div class="action-icon__container align-items-center d-flex justify-content-center mx-auto" @click="$refs[`ctxMenu-${index}`][0].open($event)">
                <i class="material-icons">more_horiz</i>
              </div>
            </td>
            <context-menu :id="`context-menu-${index}`" :ref="`ctxMenu-${index}`">
              <li v-if="determineMenuItemVisibility(row, action.condition)"
                  v-for="(action, index) in actions" :key="`context-menu-list-item#${index}`"
                  @click="$emit(action.event, row)">
                  {{ action.name }}
              </li>
            </context-menu>
          </tr>
          <tr 
            class="ml-data-table__row__extra-information" 
            v-show="(selectedRow === row) && hasCollapsable"
            :key="`datatable-row-secondary#${index}`">
            <td colspan="8">
              <div class="ml-data-table__row__extra-information__container">
                <div :class="[
                    'ml-data-table__row__extra-information__element',
                    {
                      'ml-data-table__row__extra-information__element--align-right' : (column.float === 'right'),
                      'ml-data-table__row__extra-information__element--align-left' : (column.float === 'left'),
                      'ml-data-table__row__extra-information__element--highlight': column.highlight
                    }]"
                    v-for="(column, index) in columns" :key="`extra-info#${index}`"
                    v-if="column.collapsed">
                  <div class="extra-information__title">
                    {{ column.label }}
                  </div>

                  <p class="extra-information__content">
                    {{
                      (dynamicallyFilter(row[column.key ], column.filter) === null && column.type === 'date') ?
                      '--/--/-- --:--:--' :
                      dynamicallyFilter(row[column.key ], column.filter)
                    }}
                  </p>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
    <div v-if="rows.length <= 0">
      <slot></slot>
    </div>
    <div class="ml-data-table__footer">
      <div class="ml-data-table__footer-summary">
        <span v-if="rows.length > 0">
          {{ previousRecords }} - {{ (previousRecords > 1) ? previousRecords + rows.length : rows.length }}
          <span v-show="numberOfPossiblePages <= 0">records</span>
          <span v-show="numberOfPossiblePages > 0"><span class="mx-1">of</span> many</span>
        </span>
        <span v-if="rows.length <= 0">
          0 results
        </span>
      </div>

      <div class="ml-data-table__pagination">
        <button class="ml-btn ml-btn--white ml-data-table__pagination-button ml-btn--prev" @click="prevPage">Prev</button>

        <button class="ml-btn ml-btn--white ml-data-table__pagination-button ml-btn--next" @click="nextPage">Next</button>
      </div>
    </div>
  </div>
</template>

<script>
  /* eslint-disable comma-spacing */

  import _ from 'lodash'
  import Numeral from 'numeral'
  import ContextMenu from 'vue-context-menu'
  import Popover from '@/components/Popover'
  import PhoneNumber from 'awesome-phonenumber'

  const FilterConstants = {
    string: 'like',
    number: 'between',
    date: 'dateBetween'
  }
  export default {
    components: {
      'pop-over': Popover,
      'context-menu': ContextMenu,
    },
    computed: {
      columns () {
        let columns = []
        _.keys(this.rows[0]).forEach(key => {
          columns.push({
            collapsed: ((this.columnConfiguration[key] === undefined) ? false : ((this.columnConfiguration[key].collapsed === undefined) ? false : this.columnConfiguration[key].collapsed)),
            float: ((this.columnConfiguration[key] === undefined) ? false : ((this.columnConfiguration[key].float === undefined) ? false : this.columnConfiguration[key].float)),
            filter: ((this.columnConfiguration[key] === undefined) ? false : ((this.columnConfiguration[key].filter === undefined) ? false : this.columnConfiguration[key].filter)),
            label: _.capitalize(((this.columnConfiguration[key] === undefined) ? _.lowerCase(key) : ((this.columnConfiguration[key].label === undefined) ? _.lowerCase(key) : this.columnConfiguration[key].label))),
            key: key,
            hide: ((this.columnConfiguration[key] === undefined) ? false : ((this.columnConfiguration[key].hide === undefined) ? false : this.columnConfiguration[key].hide)),
            highlight: ((this.columnConfiguration[key] === undefined) ? false : ((this.columnConfiguration[key].highlight === undefined) ? false : this.columnConfiguration[key].highlight)),
            type: ((this.columnConfiguration[key] === undefined) ? false : ((this.columnConfiguration[key].type === undefined) ? false : this.columnConfiguration[key].type))
          })
        })
        return columns
      },
      hasActions () {
        return !!this.actions.length && this.rows.length > 0
      },
      hasCollapsable () {
        let hasCollapsable = false
        _.forIn(this.columns, column => {
          if (column.collapsed) {
            hasCollapsable = true
          }
        })
        return hasCollapsable
      },
      numberOfFilters () {
        let numberOfFilters = 0

        this.tableFilters.forEach(filter => {
          if (filter.selected == null) {
            numberOfFilters++
          }
        })

        return numberOfFilters
      },
      numberOfPossiblePages () {
        if (this.page === 0 && this.rows.length < this.pageLimit) {
          return 0
        }

        return (this.page + 1)
      },
      previousRecords () {
        if (this.page === 0) {
          return 1
        }
        return this.page * this.pageLimit
      }
    },
    data () {
      return {
        filterEvent: {},
        infoEvent: {},
        outputFilters: {
          money (value) {
            return Numeral(value).format('0,0.00')
          },
          phoneNumber (value) {
            return PhoneNumber(`+${value}`).getNumber('international')
            // return `${value}`
          },
          capitalize (value) {
            return _.capitalize(value)
          }
        },
        showFilter: false,
        showInfo: false,
        tableFilters: []
      }
    },
    methods: {
      _buildTableFilters () {
        this.tableFilters = []
        
        _.forIn(this.filters, filter => {
          this.tableFilters.push({
            ...filter,
            mode: (filter.mode === undefined) ? FilterConstants[filter.type] : filter.mode,
            value: (filter.value === undefined) ? null : filter.value,
            max: (filter.max === undefined) ? null : filter.max,
            min: (filter.min === undefined) ? null : filter.min,
            selected: (filter.selected === undefined) ? null : filter.selected
          })
        })
      },
      clearFilters (event) {
        this._buildTableFilters()
        this.performFilter(event)
      },
      determineMenuItemVisibility (row, condition) {
        if (condition !== undefined) {
          if (condition.condition === 'isNot') {
            return row[condition.field] !== condition.value
          }
          return row[condition.field] === condition.value
        }
        return true
      },
      dynamicallyFilter (value, filter) {
        
        if (this.outputFilters[filter] !== undefined && !!filter) {
          return this.outputFilters[filter](value)
        }
        return value
      },
      nextPage () {
        this.$emit('next-page', { filters: this.tableFilters })
        this.$btn.disable('.ml-btn--next')
        this.$btn.disable('.ml-btn--prev')
      },
      performFilter (event) {
        this.$emit('filter', this.tableFilters)
        this.toggleFilter(event)
      },
      prevPage () {
        this.$emit('prev-page', { filters: this.tableFilters })
        this.$btn.disable('.ml-btn--next')
        this.$btn.disable('.ml-btn--prev')
      },
      toggleFilter (event) {
        this.showFilter = !this.showFilter
        this.filterEvent = event
      },
      toggleRow (row) {
        if (this.hasCollapsable) {
          this.$emit('view', row)
        }
      }
    },
    name: 'data-table',
    props: {
      actions: {
        default: function () {
          return []
        }
      },
      buttons: {
        required: false
      },
      columnConfiguration: {
        required: false
      },
      filters: {
        default: function () {
          return []
        }
      },
      selectedRow: {
        default: null
      },
      page: {
        default: 0
      },
      pageLimit: {
        default: 10
      },
      rows: {
        required: true
      }
    },
    watch: {
      filters () {
        this._buildTableFilters()
      },
      rows () {
        // Check with there're previous records
        this.page <= 0 ? this.$btn.disable('.ml-btn--prev') : this.$btn.enable('.ml-btn--prev')
        // Check whether there is a possibility of more records
        this.rows.length < this.pageLimit ? this.$btn.disable('.ml-btn--next') : this.$btn.enable('.ml-btn--next')
      }
    }
}
</script>

<style lang="scss">
@import "../styles/theme.scss";

#datatable-toolbar-left, #datatable-toolbar-right {
  display: flex;
}

#datatable-toolbar-left {
  display: flex;

  .ml-data-table__toolbar__action:first-child {
    display: flex;
    padding: 4px 8px;
    align-items: center;
    justify-content: space-between; 
    
    > i.material-icons {
      margin-right: 4px;
    }
  }
}

#datatable-toolbar-right {
  .ml-data-table__toolbar__action {
    display: flex;
    padding: 4px 8px;
    align-items: center;
    justify-content: space-between;    

    > i.material-icons {
      margin: 4px;
      font-size: 18px;
    }
  }
}

.ml-data-table {
  position: relative;

  min-height: 500px;
  padding-bottom: 3rem;
}

.ml-data-table__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;

  border-top: 3px solid $tingg-purple;
  padding: 1rem 0.25rem;
}

.ml-data-table__toolbar__action {
  background: $background-white;
  border: none;
  border-radius: 4px;
  box-shadow: 0 0 0 1px rgba(50,50,93,.1), 0 2px 5px 0 rgba(50,50,93,.08), 0 1px 1.5px 0 rgba(0,0,0,.07), 0 1px 2px 0 rgba(0,0,0,.08), 0 0 0 0 transparent;
  color: $table-text-primary;
  cursor: pointer;
  font-size: 14px;
  margin: 0 .5rem;
  padding: 4px 9px 2px;
}

.ml-data-table__toolbar__action,
.ml-data-table__toolbar__action:hover,
.ml-data-table__toolbar__action:focus {
  outline: none;
}

.ml-data-table__toolbar__action:hover {
  box-shadow: 0 0 0 1px rgba(50,50,93,.1), 0 2px 5px 0 rgba(50,50,93,.1), 0 3px 9px 0 rgba(50,50,93,.08), 0 1px 1.5px 0 rgba(0,0,0,.08), 0 1px 2px 0 rgba(0,0,0,.08);
}

.ml-data-table__placeholder {
  width: 100%;
  min-height: 400px;

  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.ml-data-table__head, .ml-data-table__row {
  border-color: $table-border-color;
}

.ml-data-table__row:last-child {
  border-bottom: 1px solid $table-border-color;
}

.ml-data-table__head {
  background-color: rgba(0, 169, 224, 1);
  
  tr > td {
    color: white !important;
  }
}

.ml-data-table__row {
  font-size: 15px;
}

.ml-data-table__row--clickable {
  cursor: pointer;
}

.ml-data-table__row--text-highlight {
  color: $tingg-blue;
  font-weight: 600;
}

.ml-data-table__row__extra-information td {
  padding: 0 !important;
}

.ml-data-table__row__extra-information__container {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  color: #1e1e1e;
  background-color: rgba(0, 169, 224, 0.5);
}

.ml-data-table__row__extra-information__element {
  width: 20%;

  align-items: flex-start;
  display: flex;
  flex-flow: column;
  justify-content: flex-start;

  font-size: 13px;
  padding: .75rem 1.5rem;

  .extra-information__title {
    font-weight: 600;
  }

  .extra-information__content {
    margin: 0;
  }
}

.ml-data-table__row__extra-information__element--align-right {
  align-items: flex-end;
}

.ml-data-table__filter {
  min-width: 250px;
  max-width: 300px;
}

.ml-data-table__filter__filters-number {
  border-left: 1px solid $border-color-lighten;
  font-weight: 600;
  margin-left: .25rem;
  padding-left: .5rem;
}

.ml-data-table__filter__head {
  background-color: $table-background-primary;
  padding: .5rem 0;
}

.ml-data-table__filter__title {
  font-size: 15px;
}

.ml-data-table__filter__body {
  padding: 0;
}

.action-icon {
  cursor: pointer;

  .action-icon__container {
    height: 1.55em;
    width: 1.55em;

    border-radius: 50%;

    &:hover {
      background: $border-color-lighten;
    }
  }
}


ul.filter-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

li.filter-list__item {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-center: space-between;

  input[type="checkbox"] {
    margin-right: .5rem;
  }

  .filter-list__item-label, .filter-list__item-form {
    padding: .75rem .5rem;
  }

  .filter-list__item-label {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .filter-list__item-form {
    background-color: $table-background-primary;
    width: 100%;
  }

  .filter-list_item-form-input-container {
    background: url('/static/img/icons/below-icon.svg') no-repeat;
    background-position: 4px 20px;
    background-size: 12px;
    padding-left: 28px;
  }
}

li.filter-list__item:not(:last-child) {
  border-bottom: 1px solid $table-border-color;
}

ul.ml-data-table__actions {
  width: 90px;
  list-style: none;
  margin: 0;
  padding: 0;
}

li.ml-data-table__action {
  cursor: pointer;
  padding: 5px 10px;
  text-align: left;
}

li.ml-data-table__action:not(:last-child) {
  border-bottom: 1px solid $table-border-color;
}

.ml-data-table__footer {
  position: absolute;
  bottom: 0;

  align-items: center;
  display: flex;
  justify-content: space-between;

  width: 100%;

  padding: 1rem .75rem;
}

.ml-data-table__footer-summary {
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/components/DataTable.vue